import {
    Component,
    ViewEncapsulation,
    Output,
    EventEmitter,
    OnDestroy,
    HostListener
} from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { interval as observableInterval, Subscription } from 'rxjs';
import { startWith, take } from 'rxjs/operators';


import { FormsUtils } from '../../resources/forms.utils';
import { AppState } from '../../interfaces/app-state.interface';
import { UsersAction } from '../../state-management/users/users.actions';
import { UsersEffects } from '../../state-management/users/users.effects';
import { TrackingActions } from './../../state-management/tracking/tracking.action';
import { LoginEvents, LoginEventType } from '../../enum/tracking/login-events.enum';
import { matchEmail } from '../../resources/custom-validators';
import { CaptchaService } from '../../services/captcha/captcha.service';
import { environment } from '../../../src/environments/environment'

const MOBILLS_PASSWORD_ROUTE_NAME = 'nova-senha-mobills';
const SANTANDER_PASSWORD_ROUTE_NAME = 'nova-senha-santander';

@AutoUnsubscribe()
@Component({
    selector: `forgot-password-form`,
    encapsulation: ViewEncapsulation.Emulated,
    templateUrl: './forgot-password-form.component.html',
    styleUrls: ['./forgot-password-form.component.scss'],
})
export class ForgotPasswordFormComponent implements OnDestroy {
    public isNewBrand: boolean = environment.isNewBrand

    @Output() public onSendRequest: EventEmitter<string> = new EventEmitter();
    public onResendToken: EventEmitter<boolean> = new EventEmitter();
    public formEmail: FormGroup;
    public formToken: FormGroup;
    public isForgotPasswordSantanderWelcomePage: boolean = false;

    public fbGroup = {
        confirmEmail: [
            '',
            Validators.compose([Validators.required, Validators.email]),],
        email: [
            '',
            Validators.compose([Validators.required, Validators.email]),
        ]
    };
    public fbTokenGroup = {
        token: ['', Validators.compose([Validators.required]),]
    }

    public validatorFbGroup = {
        validator: matchEmail(),
    };

    public pendingRequest: boolean;
    public logoPath: string = `/assets/images/${ environment.isNewBrand ? "santander" : "toro" }-total-white.svg`;
    public mobileLogoPath = `/assets/images/logos/logo-${ environment.isNewBrand ? "santander" : "toro" }-santander-white.png`
    public recaptcha: any = null;
    public reCaptchaSitekey: string;
    public hasFormEmailShow: boolean = true;
    public hasFormTokenShow: boolean = false;
    private recoverPasswordFlowSubscription: Subscription;
    private subscribeTime: Subscription;
    private authenticationSubscription: Subscription;
    public timerEmail: number = 0;
    public timerTextEmail: string = "";
    public sendByEmail: boolean;
    public sendByToken: boolean = true;
    public isMobile: boolean = false;

    private routePath: string = "";

    constructor(
        private formBuilder: FormBuilder,
        private store: Store<AppState>,
        private actions$: Actions,
        private usersEffects: UsersEffects,
        private formUtils: FormsUtils,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private captchaService: CaptchaService,
    ) {

        this.isMobile =  window.innerWidth <= 768;
        this.formEmail = this.formBuilder.group(this.fbGroup, this.validatorFbGroup);
        this.formToken = this.formBuilder.group(this.fbTokenGroup);

        this.routePath = this.activatedRoute.snapshot.routeConfig.path;

        this.isForgotPasswordSantanderWelcomePage = this.routePath == SANTANDER_PASSWORD_ROUTE_NAME;

        this.updateLogoPath();
        this._authenticationObserver();
        this._recoverPasswordObserver();
    }

    // não remover, necessário para o funcionamento correto do @AutoUnsubscribe
    // tslint:disable-next-line:no-empty
    public ngOnDestroy() { }


    @HostListener('window:resize', ['$event'])
    onResize(event: Event) {
        this.isMobile = (event.target as Window).innerWidth <= 768;
    }

    private updateLogoPath() {
        this.logoPath = !this.isForgotPasswordSantanderWelcomePage ?
        `/assets/images/${ environment.isNewBrand ? "santander" : "toro" }-total-white.svg` : `/assets/images/logos/logo-${ environment.isNewBrand ? "santander" : "toro" }-santander.png`;
    }

    public async sendEmail(event: Event = null) {
        this._trackingElement({
            elementName: "Botão continuar",
            label: "E-mail",
            scenario: this._getMetricScenario(),
            extraFields: {
                Text: this.formEmail.controls.email.value,
                "Confirme o e-mail": this.formEmail.controls.confirmEmail.value,
            }
        });

        if (event) {
            event.preventDefault();
            if(this.routePath == SANTANDER_PASSWORD_ROUTE_NAME) {
                this.isForgotPasswordSantanderWelcomePage = false;
                this.updateLogoPath();
            }

            this.sendByEmail = true;

            if (this.formEmail.valid) {

                const captcha = await this.captchaService.getForgotPasswordCaptcha();

                this.formUtils.ValidFormBaseSync(this.formEmail, () => this.store.dispatch({
                    type: UsersAction.sendForgotPasswordEmail,
                    payload: {
                        email: this.formEmail.controls.email.value,
                        captchaToken: captcha.token,
                        captchaSiteKey: captcha.siteKey,
                    },
                }));

                this.startTime();

            };
        }

    }

    public cancel() {
        if(this.routePath == SANTANDER_PASSWORD_ROUTE_NAME && this.hasFormEmailShow) {
            this.isForgotPasswordSantanderWelcomePage = true;
            this.updateLogoPath();
            return;
        }

        if (this.hasFormTokenShow) {
            this._trackingElement({
                elementName: "Botão voltar",
                label: "Código de verificação",
                scenario: this._getMetricScenario(),
                extraFields: {
                    Text: this.formToken.controls.token.value,
                },
            });

            this.hasFormEmailShow = true;
            this.hasFormTokenShow = false;
            this.sendByToken = true;
            this.formToken.reset();
            this.subscribeTime.unsubscribe();
        } else if (this.hasFormEmailShow) {
            this._trackingElement({
                elementName: "Botão voltar",
                label: "E-mail",
                scenario: this._getMetricScenario(),
                extraFields: {
                    Text: this.formEmail.controls.email.value,
                    "Confirme o e-mail": this.formEmail.controls.confirmEmail.value,
                }
            });

            this.router.navigateByUrl('/');
        }
    }

    /**
    * Verifica se os emails informados são iguais e se foram preenchidos.
    */
    public emailMatch() {
        return this.formEmail.valid;
    }

    public tokenMatch() {
        return this.formToken.valid && this.sendByToken;
    }

    public resetSendByToken() {
        if (this.formToken.valid) {
            this.sendByToken = true;
        }
    }

    public getHeaderText() {
        switch (this.routePath) {
            case MOBILLS_PASSWORD_ROUTE_NAME:
                return 'Informe o e-mail cadastrado na Mobills Investimentos';
            case SANTANDER_PASSWORD_ROUTE_NAME:
                return 'Informe o e-mail cadastrado no Banco Santander';
            default:
                return `Informe o e-mail da sua conta ${ environment.isNewBrand ? "Santander Corretora" : "Toro" }.`;
        }
    }

    public getDescriptionText() {
        switch (this.routePath) {
            case SANTANDER_PASSWORD_ROUTE_NAME:
                return `Crie seu acesso na ${ environment.isNewBrand ? "Santander Corretora" : "Toro" }`;
            default:
                return 'Alterar minha senha';
        }
    }


    private _getMetricScenario() {
        switch (this.routePath) {
            case MOBILLS_PASSWORD_ROUTE_NAME:
                return 'Tombamento Monetus';
            case SANTANDER_PASSWORD_ROUTE_NAME:
                    return 'Pré-cadastro Santander';
            default:
                return;
        }
    }

    /** Tracking Element */
    private _trackingElement({
        type = "Element Clicked",
        elementName,
        scenario,
        label,
        extraFields,
    }: {
        elementName: string,
        type?: string,
        label?: string,
        scenario?: string,
        extraFields?: { [key: string]: string }
    }) {
        this.store.dispatch({
            type: TrackingActions.trackEvents,
            payload: {
                type: type,
                events: {
                    "Product Area Origin": "Esqueci Senha",
                    "Time Responsável": "Manhattan",
                    Element: elementName,
                    Scenario: scenario,
                    Label: label,
                    ...extraFields
                },
            },
        });
    }

    private _authenticationObserver() {
        this.authenticationSubscription = this.store
            .select((s) => s.authentication)
            .subscribe((result) => {
                this.pendingRequest = result.sendingEmail;
            });

        return this;

    }

    private _recoverPasswordObserver() {
        this.recoverPasswordFlowSubscription = this.actions$
            .pipe(ofType(
                UsersAction.forgotPasswordEmailSuccefulySent,
                UsersAction.forgotPasswordEmailSendingError
            ))
            .subscribe((action: any) => {
                if (action.type === UsersAction.forgotPasswordEmailSuccefulySent) {
                    this._successNewPassWord();
                } else {
                    this._failNewPassWord();
                }
            });
    }

    private _successNewPassWord() {
        this.hasFormEmailShow = false;
        this.hasFormTokenShow = true;

        this.formEmail.updateValueAndValidity();
        this.formToken.updateValueAndValidity();

        this.store.dispatch({
            type: TrackingActions.trackTokenEvent,
            payload: {
                type: LoginEventType.Success,
                event: LoginEvents.novaSenhaEnviada,
            },
        });
    }

    private _failNewPassWord() {
        this.store.dispatch({
            type: TrackingActions.trackTokenEvent,
            payload: {
                type: LoginEventType.Error,
                event: LoginEvents.esqueciSenha,
            },
        });

        this.hasFormEmailShow = true;
        this.hasFormTokenShow = false;

        this.formEmail.updateValueAndValidity();
        this.formToken.updateValueAndValidity();
    }

    goToCreatePasswordSantander() {
        this._trackingElement({
            elementName: "Botão começar",
            scenario: this._getMetricScenario(),
            extraFields: {
                "Step": "Começar",
            }
        });

        this.isForgotPasswordSantanderWelcomePage = false;
        this.updateLogoPath();
    }

    /**
     * Envio de Token por E-mail
     * @param event Evento
     * @param link se é chamada pelo link
     */
    public sendTokenEmail(event: Event = null, link: boolean = false) {
        if (!this.timerEmail || this.timerEmail <= 0) {
            this._trackingElement({
                elementName: "Link reeviar código",
                label: "Não recebeu o código?",
                scenario: this._getMetricScenario(),
            });

            if (event) {
                event.preventDefault();
            }

            this.onResendToken.emit(!!event);
            // timer
            this.startTime();

            // request token Email
            this.sendEmail(event);
        }

        this.sendByEmail = true;
    }

    private startTime() {
        const waitTime = 300;
        this.timerEmail = waitTime;

        const source = observableInterval(1000)
            .pipe(
                startWith(0),
                take(waitTime)
            );

        this.subscribeTime = source.subscribe(() => this.toHoursAndMinutes(--this.timerEmail), () => null, () => this.subscribeTime.unsubscribe());
    }

    public async goStepPasswordByToken(event: Event = null) {
        this._trackingElement({
            elementName: "Botão continuar",
            label: "Código de verificação",
            scenario: this._getMetricScenario(),
            extraFields: {
                Text: this.formToken.controls.token.value,
            },
        });

        this.sendByToken = true;

        if (event) {
            event.preventDefault();

            if (this.formToken.invalid) return;

            this.formUtils.ValidFormBaseSync(this.formEmail, () => this.store.dispatch({
                type: UsersAction.validateTokenForgotPasswordEmail,
                payload: {
                    email: this.formEmail.controls.email.value,
                    token: this.formToken.controls.token.value,
                    originPath: this.routePath,
                },
            }));

            this.sendByToken = false;
        }
    }

    private toHoursAndMinutes(totalSeconds: number) {
        const totalMinutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        const minutes = totalMinutes % 60;

        this.timerTextEmail = `${minutes > 0 ? "0" + minutes + ":" : ""}${seconds < 10 ? "0" + seconds : seconds}`;
    }
}


